<template>
  <div class="customer-management-container">
    <!-- 功能区 -->
    <div class="action-bar">
      <div class="search-section">
        <el-input
          v-model="searchKeyword"
          placeholder="请输入客户姓名、手机号、ID或质保卡编号"
          clearable
          @clear="handleSearch"
          @keyup.enter.native="handleSearch"
          class="search-input"
        >
          <el-button slot="append" icon="el-icon-search" @click="handleSearch">搜索</el-button>
        </el-input>
        
        <el-upload
          action=""
          :auto-upload="false"
          :show-file-list="false"
          :on-change="handleFileImport"
          accept=".xlsx,.xls,.csv"
          class="import-btn"
        >
          <el-button type="primary" icon="el-icon-upload">客户导入</el-button>
        </el-upload>
        <!-- 新增客户按钮 -->
        <el-button 
          type="primary" 
          icon="el-icon-plus" 
          @click="openAddDialog"
          style="margin-left:30px"
        >
          新增客户
        </el-button>
      </div>
      
      <div class="action-section">
        <el-button icon="el-icon-refresh" @click="refreshData">刷新</el-button>
      </div>
    </div>
    
    <!-- 数据表格 -->
    <div class="data-table">
      <el-table
        :data="filteredCustomers"
        stripe
        style="width: 100%"
        v-loading="loading"
        :header-cell-style="{background:'#f5f7fa', fontWeight: 'bold'}"
      >
        <el-table-column prop="id" label="ID" width="80" sortable align="center">
          <template slot-scope="scope">
            <span class="id-cell">#{{ scope.row.id }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="客户姓名" min-width="120" align="center">
          <template slot="header">
            <div class="flex-center">
              <i class="el-icon-user"></i>
              <span>客户姓名</span>
            </div>
          </template>
          <template slot-scope="scope">
            <div>{{ scope.row.name }}</div>
          </template>
        </el-table-column>
        
        <el-table-column prop="phone" label="手机号" width="140" align="center">
          <template slot="header">
            <div class="flex-center">
              <i class="el-icon-phone"></i>
              <span>手机号</span>
            </div>
          </template>
        </el-table-column>
        
        <el-table-column label="OpenID" min-width="120" align="center">
          <template slot="header">
            <div class="flex-center">
              <i class="el-icon-key"></i>
              <span>OpenID</span>
            </div>
          </template>
          <template slot-scope="scope">
            <div class="openid-cell">
              <el-tooltip :content="scope.row.openid" placement="top">
                <span>{{ truncateOpenID(scope.row.openid) }}</span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="notes" label="客户备注" width="240" align="center">
          <template slot="header">
            <div class="flex-center">
              <i class="el-icon-postcard"></i>
              <span>备注</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="cardNumber" label="质保卡编号" width="180" align="center">
          <template slot="header">
            <div class="flex-center">
              <i class="el-icon-s-claim"></i>
              <span>质保卡编号</span>
            </div>
          </template>
          <template slot-scope="scope">
            <div v-if="scope.row.cards && scope.row.cards.length > 0">
              {{ scope.row.cards[0].cardNumber }}
              <el-tooltip 
                v-if="scope.row.cards.length > 1" 
                :content="`共${scope.row.cards.length}张质保卡`"
                placement="top"
              >
                <el-tag size="mini">+{{ scope.row.cards.length - 1 }}</el-tag>
              </el-tooltip>
            </div>
            <div v-else>--</div>
          </template>
        </el-table-column>
        
        <el-table-column label="质保卡信息" width="180" align="center">
          <template slot="header">
            <div class="flex-center">
              <i class="el-icon-s-claim"></i>
              <span>质保卡信息</span>
            </div>
          </template>
          <template slot-scope="scope">
            <el-button 
              type="primary" 
              size="mini"
              @click="showWarrantyDetails(scope.row)"
            >
              查看详情
            </el-button>
          </template>
        </el-table-column>
        
        <el-table-column label="操作" width="420" fixed="right" align="center">
          <template slot="header">
            <div class="flex-center">
              <i class="el-icon-setting"></i>
              <span>操作</span>
            </div>
          </template>
          <template slot-scope="scope">
            <!-- 编辑按钮 -->
            <el-button
              type="warning"
              size="small"
              icon="el-icon-edit"
              @click="openEditDialog(scope.row)"
            >编辑</el-button>
            <el-button
              type="primary"
              size="small"
              icon="el-icon-copy-document"
              @click="showQRCode(scope.row)"
            >二维码</el-button>
            <el-button
              type="danger"
              size="small"
              icon="el-icon-delete"
              @click="deleteCustomer(scope.row)"
            >删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 分页控件 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 30, 40]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pageTotal"
          background
        >
        </el-pagination>
      </div>
    </div>
    
    <!-- 空状态提示 -->
    <div v-if="filteredCustomers.length === 0 && !loading" class="empty-table">
      <div class="empty-content">
        <i class="el-icon-folder-opened"></i>
        <p>暂无客户数据</p>
        <el-button type="primary" @click="refreshData">刷新数据</el-button>
      </div>
    </div>
    
    <!-- 质保卡详情对话框 -->
    <WarrantyDetailsDialog 
      :visible.sync="warrantyDialogVisible"
      :customer="currentCustomer"
      @refresh="refreshCustomers"
    />
    
    <!-- 二维码对话框 -->
    <QrCodeDialog 
      :visible.sync="qrDialogVisible"
      :customer="currentCustomer"
      @refresh="refreshQrCode"
    />
    
    <!-- 新增/编辑客户对话框 -->
    <CustomerFormDialog 
      :visible.sync="customerDialogVisible"
      :type="dialogType"
      :form="customerForm"
      @submit="submitCustomerForm"
      @closed="resetForm"
    />
  </div>
</template>

<script>
import { createCustomer, getCustomers, updateCustomers, delCustomer, GenQrcode } from '../api/admin'
import WarrantyDetailsDialog from './WarrantyDetailsDialog'
import QrCodeDialog from './QrCodeDialog'
import CustomerFormDialog from './CustomerFormDialog'

export default {
  name: 'CustomerManagement',
  components: {
    WarrantyDetailsDialog,
    QrCodeDialog,
    CustomerFormDialog
  },
  data() {
    return {
      searchKeyword: '',
      loading: false,
      currentPage: 1,
      pageSize: 10,
      pageTotal: 0,
      customers: [],
      currentCustomer: {}, // 当前选中的客户
      customerDialogVisible: false,
      dialogType: 'add', // 'add' 或 'edit'
      customerForm: {
        id: null,
        name: '',
        phone: '',
        openid: '',
        type: 'normal',
        remark: ''
      },
      warrantyDialogVisible: false,
      qrDialogVisible: false
    }
  },
  computed: {
    filteredCustomers() {
      if (!this.searchKeyword) {
        return this.customers;
      }
      const keyword = this.searchKeyword.toLowerCase();
      return this.customers.filter(customer => {
        // 检查客户基本信息
        const basicMatch = (
          String(customer.id).includes(keyword) ||
          customer.name.toLowerCase().includes(keyword) ||
          customer.phone.includes(keyword) ||
          (customer.openid && customer.openid.toLowerCase().includes(keyword))
        );
        
        // 如果基本信息匹配，直接返回
        if (basicMatch) return true;
        
        // 检查质保卡编号
        if (customer.cards) {
          return customer.cards.some(card => 
            card.cardNumber && card.cardNumber.toLowerCase().includes(keyword)
          );
        }
        
        return false;
      });
    }
  },
  methods: {
    // 打开新增客户对话框
    openAddDialog() {
      this.dialogType = 'add';
      this.customerDialogVisible = true;
    },
    
    // 打开编辑客户对话框
    openEditDialog(customer) {
      this.dialogType = 'edit';
      this.customerForm = {
        id: customer.id,
        name: customer.name,
        phone: customer.phone,
        openid: customer.openid || '',
        remark: customer.notes || ''
      };
      this.customerDialogVisible = true;
    },
    
    // 提交客户表单（新增/编辑）
    submitCustomerForm() {
      if (this.dialogType === 'add') {
        createCustomer({
          nickname: this.customerForm.name,
          phoneNum: this.customerForm.phone,
          Notes: this.customerForm.remark
        }).then(res => {
          if (res.code == 1) {
            this.$message.success(`客户 ${this.customerForm.name} 添加成功`);
            this.refreshCustomers()
          }
        }).catch(err => {
          this.$message.error(err.error)
        })
      } else {
        updateCustomers(this.customerForm.id, {
          nickname: this.customerForm.name,
          phoneNum: this.customerForm.phone,
          Notes: this.customerForm.remark
        }).then(res => {
          if (res.code == 1) {
            this.$message.success(`客户 ${this.customerForm.name} 信息更新成功`);
            this.refreshCustomers()
          }
        }).catch(err => {
          this.$message.error(err.error)
        })
      }
      
      this.customerDialogVisible = false;
    },
    
    // 重置表单
    resetForm() {
      this.customerForm = {
        id: null,
        name: '',
        phone: '',
        openid: '',
        type: 'normal',
        remark: ''
      };
    },
    
    handleSearch() {
      this.loading = true;
      this.currentPage = 1; // 搜索时重置到第一页
      setTimeout(() => {
        this.loading = false;
      }, 300);
    },
    
    handleFileImport(file) {
      console.log('导入文件:', file.name);
      this.$message.info('导入请求已发送（后端功能未实现）');
    },
    
    refreshData() {
      this.refreshCustomers();
      this.$message.info("已刷新")
    },
    
    // 显示质保卡详情
    showWarrantyDetails(customer) {
      this.currentCustomer = customer;
      this.warrantyDialogVisible = true;
    },
    
    // 显示二维码
    showQRCode(customer) {
      this.currentCustomer = customer;
      this.qrDialogVisible = true;
    },
    
    deleteCustomer(customer) {
      this.$confirm(`确定要删除客户 ${customer.name} 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        delCustomer(customer.id).then(res => {
          if(res.code == 1) {
            this.$message.success('删除成功');
            this.refreshCustomers();
          }
        })
      }).catch(() => {
        this.$message.info('操作已取消');
      });
    },
    
    handleSizeChange(size) {
      this.pageSize = size;
      this.refreshCustomers();
    },
    
    handleCurrentChange(page) {
      this.currentPage = page;
      this.refreshCustomers();
    },
    
    truncateOpenID(openid) {
      if (!openid) return '';
      return openid.substring(0, 10) + '...' + openid.substring(openid.length - 6);
    },

    refreshCurrentCustomer(){
      if(this.currentCustomer)
      {
        let cur = this.customers.find(it=> it.id == this.currentCustomer.id)
        if(cur)
        {
          this.currentCustomer = cur
        }else{
          this.currentCustomer = {}
        }

      }
    },
    
    refreshCustomers() {
      this.loading = true
      getCustomers({page: this.currentPage, pageSize: this.pageSize}).then(res => {
        if(res.code == 1) {
          this.customers = []
          if(res.data.list) {
            for(var i = 0; i < res.data.list.length; i++) {
              var cards = []
              for(var j = 0; j < res.data.list[i].Cards.length; j++) {
                let imagesArr = []
                let images = []
                if(res.data.list[i].Cards[j].ImageURL != null) {
                  images = JSON.parse(res.data.list[i].Cards[j].ImageURL)
                }
                images.forEach(item => imagesArr.push({url: process.env.VUE_APP_BASE_API + "\\uploads\\" + item, file: null, originUrl: item}))

                cards.push({
                  projectId: res.data.list[i].Cards[j].ID,
                  projectName: res.data.list[i].Cards[j].Name,
                  projectAddress: res.data.list[i].Cards[j].Address,
                  storeName: res.data.list[i].Cards[j].WarrantyShop,
                  storeAddress: res.data.list[i].Cards[j].WarrantyShopAddress,
                  cardNumber: res.data.list[i].Cards[j].CardNumber,
                  isExpanded: false,
                  warranties: JSON.parse(res.data.list[i].Cards[j].Content),
                  effectImages: imagesArr,
                  contract: res.data.list[i].Cards[j].FileURL !== "" ? process.env.VUE_APP_BASE_API + "\\uploads\\" + res.data.list[i].Cards[j].FileURL : "",
                  orginContract: res.data.list[i].Cards[j].FileURL
                })
              }
              this.customers.push({
                id: res.data.list[i].ID, 
                name: res.data.list[i].Nickname, 
                phone: res.data.list[i].PhoneNum,
                openid: res.data.list[i].OpenID,
                notes: res.data.list[i].Notes,
                qrCodePath: res.data.list[i].QrCodePath !== "" ? process.env.VUE_APP_BASE_API + "\\" + res.data.list[i].QrCodePath : "", 
                qrCodeExpireAt: res.data.list[i].QrCodeExpireAt * 1000,
                cards: cards
              })
            }
          }
          this.refreshCurrentCustomer()
          this.pageTotal = res.data.total
        }
      }).catch(err => {
        this.$message.error(err.error)
      }).finally(() => {
        this.loading = false
      })
    },
    
    refreshQrCode(id) {
      this.$confirm('确定要重新生成二维码吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        GenQrcode({customer_id: id}).then(res => {
          if(res.code == 1) {
            this.$message.success("刷新成功")
            this.refreshCustomers();
          }
        }).catch(err => {
          console.log("刷新二维码错误", err)
          this.$message.error(err);
        })
      }).catch(() => {
        this.$message.info('操作已取消');
      });
    }
  },
  mounted() {
    this.refreshCustomers();
  }
}
</script>

<style scoped>
.customer-management-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

/* 功能区样式 */
.action-bar {
  background-color: white;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
}

.action-bar .search-section {
  display: flex;
  align-items: center;
}

.action-bar .search-section .search-input {
  width: 300px;
  margin-right: 15px;
}

.action-bar .search-section .import-btn {
  margin-left: 10px;
}

.action-bar .action-section {
  display: flex;
}

.action-bar .action-section .el-button {
  margin-left: 10px;
}

/* 数据表格样式 */
.data-table {
  background-color: white;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 21, 41, 0.08);
  flex: 1;
  display: flex;
  flex-direction: column;
}

.data-table .flex-center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.data-table .id-cell {
  font-weight: bold;
  color: #409eff;
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}

/* 空状态提示样式 */
.empty-table {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.empty-table .empty-content i {
  font-size: 60px;
  color: #c0c4cc;
  margin-bottom: 20px;
}

.empty-table .empty-content p {
  color: #909399;
  font-size: 16px;
  margin-bottom: 20px;
}
</style>